import { useState, useEffect } from 'react'
import { sharepointServiceProduction } from '../utils/sharepointServiceProduction'
import { sharepointImageService } from '../utils/sharepointImageService'
import { clientImageService } from '../utils/clientImageService'

interface SharePointStatus {
  connected: boolean
  message: string
  lastSync?: Date
}

interface DataStats {
  classes: number
  kits: number
  gallery: number
  images: number
}

function UnifiedSharePointManager() {
  const [activeSection, setActiveSection] = useState<'connection' | 'data' | 'images'>('connection')
  const [status, setStatus] = useState<SharePointStatus>({
    connected: false,
    message: 'Not connected'
  })
  const [isLoading, setIsLoading] = useState(false)
  const [operationStatus, setOperationStatus] = useState<string>('')
  const [dataStats, setDataStats] = useState<DataStats>({ classes: 0, kits: 0, gallery: 0, images: 0 })

  // Connection & Setup Methods
  const testConnection = async () => {
    setIsLoading(true)
    setOperationStatus('Testing SharePoint connection...')

    try {
      const result = await sharepointServiceProduction.testConnection()
      setStatus({
        connected: result.success,
        message: result.message
      })
      setOperationStatus(result.message)
    } catch (error) {
      const errorMessage = `Connection failed: ${(error as Error).message}`
      setStatus({ connected: false, message: errorMessage })
      setOperationStatus(errorMessage)
    }

    setIsLoading(false)
  }

  const createLists = async () => {
    setIsLoading(true)
    setOperationStatus('Creating SharePoint lists...')

    try {
      const result = await sharepointServiceProduction.createLists()
      setOperationStatus(result.message)
    } catch (error) {
      setOperationStatus(`❌ Error creating lists: ${(error as Error).message}`)
    }

    setIsLoading(false)
  }

  // Data Sync Methods (from working AdminDataManager)
  const loadDataStats = () => {
    try {
      const classesData = localStorage.getItem('salty_mermaid_classes')
      const kitsData = localStorage.getItem('salty_mermaid_kits')
      const galleryData = localStorage.getItem('salty_mermaid_gallery_items')
      const imagesData = localStorage.getItem('salty_mermaid_images')

      setDataStats({
        classes: classesData ? JSON.parse(classesData).length : 0,
        kits: kitsData ? JSON.parse(kitsData).length : 0,
        gallery: galleryData ? JSON.parse(galleryData).length : 0,
        images: imagesData ? JSON.parse(imagesData).length : 0
      })
    } catch (error) {
      console.error('Error loading data stats:', error)
    }
  }

  const syncFromSharePoint = async () => {
    setIsLoading(true)
    setOperationStatus('Syncing from SharePoint...')

    try {
      // Sync classes, kits, and gallery from SharePoint
      setOperationStatus('Syncing classes, kits, and gallery from SharePoint...')
      const dataResult = await sharepointServiceProduction.loadFromSharePoint()

      // Sync images - first load all images from SharePoint, then sync them
      setOperationStatus('Loading all images from SharePoint...')
      const allImages = await sharepointImageService.getImages()
      setOperationStatus('Processing and syncing images...')

      // Download SharePoint images to client-side cache
      setOperationStatus('Downloading images to client cache...')
      const clientImages = await clientImageService.processSharePointImages(allImages)

      // Convert to format expected by public site (using blob URLs)
      const processedImages = clientImages.map(img => ({
        id: img.id,
        filename: img.name,
        dataUrl: img.blobUrl, // Use blob URL for immediate display
        category: img.category,
        uploadDate: img.lastModified
      }))

      // Update localStorage with processed images for both admin and public use
      localStorage.setItem('salty_mermaid_images', JSON.stringify(processedImages))
      localStorage.setItem('salty_mermaid_public_images', JSON.stringify(processedImages))

      // Also populate public cache for classes, kits, and gallery so public site works immediately
      // Public cache now reads directly from admin storage - no need to duplicate
      setOperationStatus('Data synced - public cache will read from admin storage...')

      const imagesResult = {
        success: true,
        message: `Successfully loaded ${allImages.length} images from SharePoint`,
        syncedCount: allImages.length
      }

      // Data sync completed successfully

      // Validate sync results
      const postSyncStats = await validateSyncResults()

      setOperationStatus(`✅ SharePoint sync complete:
Data: ${dataResult.success ? 'Success' : 'Failed'}
Images: ${imagesResult.success ? 'Success' : 'Failed'}

📊 Sync Validation:
Classes: ${postSyncStats.classes} items synced
Kits: ${postSyncStats.kits} items synced
Gallery: ${postSyncStats.gallery} items synced
Images: ${postSyncStats.images} items synced`)

      loadDataStats()
    } catch (error) {
      setOperationStatus('❌ Error syncing from SharePoint: ' + (error as Error).message)
    }

    setIsLoading(false)
  }

  const syncToSharePoint = async () => {
    setIsLoading(true)
    setOperationStatus('Syncing to SharePoint...')

    try {
      const result = await sharepointServiceProduction.syncToSharePoint()
      setOperationStatus(result.message)
      if (result.success) {
        setStatus(prev => ({ ...prev, lastSync: new Date() }))
      }
    } catch (error) {
      setOperationStatus(`❌ Error syncing to SharePoint: ${(error as Error).message}`)
    }

    setIsLoading(false)
  }

  const reliableSync = async (maxRetries = 3) => {
    setIsLoading(true)

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        setOperationStatus(`🔄 Reliable sync attempt ${attempt}/${maxRetries}...`)

        const dataResult = await sharepointServiceProduction.loadFromSharePoint()

        // Load and download images from SharePoint to client cache
        const allImages = await sharepointImageService.getImages()
        const clientImages = await clientImageService.processSharePointImages(allImages)

        // Convert to format expected by public site (using blob URLs)
        const processedImages = clientImages.map(img => ({
          id: img.id,
          filename: img.name,
          dataUrl: img.blobUrl, // Use blob URL for immediate display
          category: img.category,
          uploadDate: img.lastModified
        }))
        localStorage.setItem('salty_mermaid_images', JSON.stringify(processedImages))
        localStorage.setItem('salty_mermaid_public_images', JSON.stringify(processedImages))

        const imagesResult = {
          success: true,
          message: `Successfully loaded ${allImages.length} images from SharePoint`,
          syncedCount: allImages.length
        }

        if (dataResult.success && imagesResult.success) {
          const stats = await validateSyncResults()

          setOperationStatus(`✅ Reliable sync successful on attempt ${attempt}:
📊 Data synced: Classes: ${stats.classes}, Kits: ${stats.kits}, Gallery: ${stats.gallery}, Images: ${stats.images}`)

          loadDataStats()
          setIsLoading(false)
          return
        } else {
          throw new Error(`Data sync: ${dataResult.success ? 'OK' : 'Failed'}, Images sync: ${imagesResult.success ? 'OK' : 'Failed'}`)
        }
      } catch (error) {
        console.error(`Sync attempt ${attempt} failed:`, error)

        if (attempt === maxRetries) {
          setOperationStatus(`❌ Reliable sync failed after ${maxRetries} attempts: ${(error as Error).message}`)
        } else {
          setOperationStatus(`⚠️ Attempt ${attempt} failed, retrying in 2 seconds...`)
          await new Promise(resolve => setTimeout(resolve, 2000))
        }
      }
    }

    setIsLoading(false)
  }

  // Helper methods

  const validateSyncResults = async () => {
    try {
      const classesLS = localStorage.getItem('salty_mermaid_classes')
      const kitsLS = localStorage.getItem('salty_mermaid_kits')
      const galleryLS = localStorage.getItem('salty_mermaid_gallery_items')
      const imagesLS = localStorage.getItem('salty_mermaid_images')

      return {
        classes: classesLS ? JSON.parse(classesLS).length : 0,
        kits: kitsLS ? JSON.parse(kitsLS).length : 0,
        gallery: galleryLS ? JSON.parse(galleryLS).length : 0,
        images: imagesLS ? JSON.parse(imagesLS).length : 0
      }
    } catch (error) {
      console.error('Error validating sync results:', error)
      return { classes: 0, kits: 0, gallery: 0, images: 0 }
    }
  }

  // Load data on component mount
  useEffect(() => {
    loadDataStats()
  }, [])

  const getConfig = () => {
    const getEnvVar = (key: string) => {
      try {
        const viteKey = key.replace('REACT_APP_', 'VITE_')
        return (import.meta.env && (import.meta.env[viteKey] || import.meta.env[key])) ||
               (typeof process !== 'undefined' && process.env && process.env[key]) ||
               ''
      } catch {
        return ''
      }
    }

    return {
      tenantId: getEnvVar('REACT_APP_SHAREPOINT_TENANT_ID'),
      clientId: getEnvVar('REACT_APP_SHAREPOINT_CLIENT_ID'),
      siteUrl: getEnvVar('REACT_APP_SHAREPOINT_SITE_URL'),
      enabled: getEnvVar('REACT_APP_SHAREPOINT_ENABLED') === 'true'
    }
  }

  const config = getConfig()

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="fs-3 fw-bold text-dark">SharePoint Management</h2>
          <p className="text-muted mb-0">Unified SharePoint connection, data sync, and image management</p>
        </div>
      </div>

      {/* Section Navigation */}
      <div className="mb-4">
        <ul className="nav nav-pills">
          <li className="nav-item">
            <button
              className={`nav-link ${activeSection === 'connection' ? 'active' : ''}`}
              onClick={() => setActiveSection('connection')}
            >
              ☁️ Connection & Setup
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeSection === 'data' ? 'active' : ''}`}
              onClick={() => setActiveSection('data')}
            >
              🔄 Data Synchronization
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeSection === 'images' ? 'active' : ''}`}
              onClick={() => setActiveSection('images')}
            >
              📷 Image Management
            </button>
          </li>
        </ul>
      </div>

      {/* Connection & Setup Section */}
      {activeSection === 'connection' && (
        <div className="row g-4">
          <div className="col-md-6">
            <div className="card">
              <div className="card-header">
                <h5 className="card-title mb-0">Connection Status</h5>
              </div>
              <div className="card-body">
                <div className={`alert ${status.connected ? 'alert-success' : 'alert-warning'}`}>
                  <div className="d-flex align-items-center">
                    <span className="me-2">{status.connected ? '✅' : '⚠️'}</span>
                    <div>
                      <strong>{status.connected ? 'Connected' : 'Disconnected'}</strong>
                      <div className="small">{status.message}</div>
                    </div>
                  </div>
                </div>

                <div className="mb-3">
                  <small className="text-muted">Configuration:</small>
                  <div className="small">
                    <div>Tenant ID: {config.tenantId ? '✅ Set' : '❌ Missing'}</div>
                    <div>Client ID: {config.clientId ? '✅ Set' : '❌ Missing'}</div>
                    <div>Site URL: {config.siteUrl ? '✅ Set' : '❌ Missing'}</div>
                    <div>Enabled: {config.enabled ? '✅ Yes' : '❌ No'}</div>
                  </div>
                </div>

                <div className="d-grid gap-2">
                  <button
                    onClick={testConnection}
                    disabled={isLoading}
                    className="btn btn-primary"
                  >
                    Test Connection
                  </button>
                  <button
                    onClick={createLists}
                    disabled={isLoading || !status.connected}
                    className="btn btn-outline-primary"
                  >
                    Create SharePoint Lists
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-6">
            <div className="card">
              <div className="card-header">
                <h5 className="card-title mb-0">Configuration Help</h5>
              </div>
              <div className="card-body">
                <p className="small text-muted">
                  To configure SharePoint integration, create a <code>.env</code> file with:
                </p>
                <pre className="bg-light p-2 small">
{`REACT_APP_SHAREPOINT_TENANT_ID=your-tenant-id
REACT_APP_SHAREPOINT_CLIENT_ID=your-client-id
REACT_APP_SHAREPOINT_SITE_URL=https://your.sharepoint.com/sites/SiteName
REACT_APP_SHAREPOINT_ENABLED=true`}
                </pre>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Data Synchronization Section */}
      {activeSection === 'data' && (
        <div>
          {/* Current Data Stats */}
          <div className="row mb-4">
            <div className="col-12">
              <h6 className="fw-bold">Current Data Statistics</h6>
              <div className="row text-center">
                <div className="col-3">
                  <div className="bg-primary-subtle p-2 rounded">
                    <div className="fw-bold text-primary">{dataStats.classes}</div>
                    <small>Classes</small>
                  </div>
                </div>
                <div className="col-3">
                  <div className="bg-success-subtle p-2 rounded">
                    <div className="fw-bold text-success">{dataStats.kits}</div>
                    <small>Kits</small>
                  </div>
                </div>
                <div className="col-3">
                  <div className="bg-info-subtle p-2 rounded">
                    <div className="fw-bold text-info">{dataStats.gallery}</div>
                    <small>Gallery</small>
                  </div>
                </div>
                <div className="col-3">
                  <div className="bg-warning-subtle p-2 rounded">
                    <div className="fw-bold text-warning">{dataStats.images}</div>
                    <small>Images</small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Operation Status */}
          {operationStatus && (
            <div className="alert alert-info mb-4">
              <pre className="mb-0" style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}>
                {operationStatus}
              </pre>
            </div>
          )}

          {/* Sync Operations */}
          <div className="row g-3">
            <div className="col-md-6">
              <div className="card border-success">
                <div className="card-header bg-success-subtle">
                  <h6 className="card-title mb-0 text-success">⬇️ Pull from SharePoint</h6>
                </div>
                <div className="card-body">
                  <div className="d-grid gap-2">
                    <button
                      onClick={syncFromSharePoint}
                      disabled={isLoading || !status.connected}
                      className="btn btn-success"
                    >
                      Sync from SharePoint
                    </button>
                    <button
                      onClick={() => reliableSync(3)}
                      disabled={isLoading || !status.connected}
                      className="btn btn-primary"
                    >
                      Reliable Sync (3 attempts)
                    </button>
                    <button
                      onClick={loadDataStats}
                      disabled={isLoading}
                      className="btn btn-outline-success"
                    >
                      Refresh Statistics
                    </button>
                  </div>
                  <small className="text-muted mt-2 d-block">
                    ⬇️ Downloads data from SharePoint to local storage
                  </small>
                </div>
              </div>
            </div>

            <div className="col-md-6">
              <div className="card border-primary">
                <div className="card-header bg-primary-subtle">
                  <h6 className="card-title mb-0 text-primary">⬆️ Push to SharePoint</h6>
                </div>
                <div className="card-body">
                  <div className="d-grid gap-2">
                    <button
                      onClick={syncToSharePoint}
                      disabled={isLoading || !status.connected}
                      className="btn btn-primary"
                    >
                      Upload to SharePoint
                    </button>
                  </div>
                  <small className="text-muted mt-2 d-block">
                    ⬆️ Uploads current data from admin panel to SharePoint lists
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Management Section */}
      {activeSection === 'images' && (
        <div className="card">
          <div className="card-header">
            <h5 className="card-title mb-0">SharePoint Image Management</h5>
          </div>
          <div className="card-body">
            <div className="alert alert-info">
              <strong>Image Management:</strong> Use the individual gallery, classes, and kits tabs to manage images for each content type. Images are automatically synced with SharePoint when you upload or assign them to content items.
            </div>
            <div className="text-center py-4">
              <div className="display-4 mb-3">📷</div>
              <p className="text-muted">
                SharePoint image management is integrated into each content section.<br/>
                Visit the Classes, Kits, or Gallery tabs to upload and manage images.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Loading Spinner */}
      {isLoading && (
        <div className="text-center mt-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <div className="mt-2">
            <small className="text-muted">Processing operation...</small>
          </div>
        </div>
      )}
    </div>
  )
}

export default UnifiedSharePointManager